import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import PublicComponent from "../PublicComponent";
import { PUBLIC_DATA_COMPLETION_QUERY } from "../../queries";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { emptyArray, emptyString } from "constants/defaultValues";
import { set, useStateLens, sequence, get } from "@totalsoft/react-state-lens";
import RequiredDocumentComponent from "features/dataCompletion/components/RequiredDocumentComponent";
import { Card, LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import publicMainStyle from "assets/jss/components/publicMainStyles";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Grid } from "@material-ui/core";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { CONFIRM_COMPLETION_UPLOAD_FILES } from "../../mutations";
import ActionsDialog from "features/common/components/ActionsDialog";
import { useTranslation } from "react-i18next";
import { quoteStatus } from "constants/quoteStatus";

const useStyles = makeStyles(publicMainStyle);

const PublicDataCompletion = (props) => {
  const { stateLens } = props;
  const quoteId = stateLens.data.quoteId |> get;
  const requiredDocumentLens = useStateLens(emptyArray);
  const classes = useStyles();
  const { t } = useTranslation();
  const [quoteStatusId, setQuoteStatusId] = useState(null);
  const [actionsDialogProps, setActionsDialogProps] = useState({
    open: false,
    title: emptyString,
    message: emptyString,
    actions: emptyArray,
  });
  const handleCloseActionsDialog = useCallback(() => setActionsDialogProps((prev) => ({ ...prev, open: false })), []);

  const { loading } = useQueryWithErrorHandling(PUBLIC_DATA_COMPLETION_QUERY, {
    variables: { quoteId: parseInt(quoteId) },
    onCompleted: (data) => {
      if (!data) return;
      (requiredDocumentLens |> set)(data.publicDataCompletion.requiredDocuments);
      setQuoteStatusId(data.publicDataCompletion.quoteStatusId);
    },
  });

  const disabled = useMemo(() => {
    return quoteStatusId !== quoteStatus.pendingCustomerInfo.id;
  }, [quoteStatusId]);

  const [confirmCompletionUploadedFiles] = useMutationWithErrorHandling(CONFIRM_COMPLETION_UPLOAD_FILES, {
    onCompleted: (data) => {
      if (!data) return;
      if (data.confirmCompletionUploadedFiles === true) setQuoteStatusId(quoteStatus.pendingDecision.id);
      setActionsDialogProps({
        open: true,
        title:
          data.confirmCompletionUploadedFiles === true
            ? t("DocumentsReceivedConfirmation.DocumentsReceived")
            : t("DocumentsReceivedConfirmation.ProvideOneDocumentPerCategory"),
        message:
          data.confirmCompletionUploadedFiles === true
            ? t("DocumentsReceivedConfirmation.RequestedDocumentsReceived")
            : t("DocumentsReceivedConfirmation.ReturnToThisPageAtAnytime"),
        actions: [
          {
            caption: t("General.OK"),
            handle: () => {
              return true;
            },
          },
        ],
      });
    },
  });

  const handleConfirmCompletionUploadedFiles = useCallback(() => {
    confirmCompletionUploadedFiles({
      variables: {
        quoteId: quoteId,
      },
    });
  }, [confirmCompletionUploadedFiles, quoteId]);

  return (
    <Card className={classes.card}>
      <Grid container spacing={3}>
        {loading && <LoadingFakeText lines={5} />}
        <Grid item xs={12}>
          {!loading &&
            (requiredDocumentLens |> sequence).map(
              (requiredDocumentLens, index) =>
                ((requiredDocumentLens.isMandatory |> get) || (requiredDocumentLens.isVisible |> get)) && (
                  <RequiredDocumentComponent
                    key={index}
                    index={index}
                    requiredDocumentLens={requiredDocumentLens}
                    quoteId={parseInt(quoteId)}
                    disabled={disabled}
                  />
                )
            )}
        </Grid>
        <Grid item xs={12}>
          {!loading && (
            <Button
              className={classes.doneButton}
              variant="contained"
              color="primary"
              onClick={handleConfirmCompletionUploadedFiles}
              disabled={disabled}
            >
              {t("DocumentsReceivedConfirmation.Done")}
            </Button>
          )}
        </Grid>
        <ActionsDialog
          open={actionsDialogProps.open}
          handleClose={handleCloseActionsDialog}
          title={actionsDialogProps.title}
          message={actionsDialogProps.message}
          actions={actionsDialogProps.actions}
          Content={actionsDialogProps.Content}
        />
      </Grid>
    </Card>
  );
};

PublicDataCompletion.propTypes = {
  stateLens: PropTypes.object.isRequired,
};

export default () => <PublicComponent Component={PublicDataCompletion} />;
